
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Phone, Mail, MapPin, Clock, Menu, X } from 'lucide-react';

const Contact = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms) {
      alert('Please agree to the Terms and Privacy Policy to continue.');
      return;
    }
    if (!formData.email.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }
    // Simulate form submission
    console.log('Contact form submitted:', formData);
    window.location.href = '/success';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-30">
        <nav className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-yellow-300 rounded transform rotate-12"></div>
              <h1 className="text-2xl font-bold text-yellow-600">GoldenTicketLucky</h1>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <a href="/" className="text-gray-700 hover:text-yellow-600 transition-colors">Home</a>
              <a href="/about" className="text-gray-700 hover:text-yellow-600 transition-colors">About</a>
              <a href="/results" className="text-gray-700 hover:text-yellow-600 transition-colors">Results</a>
              
              <a href="/contact" className="text-yellow-600 font-medium">Contact</a>
              <div className="relative group">
                <button className="text-gray-700 hover:text-yellow-600 transition-colors">Legal</button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <a href="/terms" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Terms</a>
                  <a href="/privacy" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Privacy</a>
                  <a href="/cookies" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Cookies</a>
                  <a href="/accessibility" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Accessibility</a>
                  <a href="/notices" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Notices</a>
                </div>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 space-y-2">
              <a href="/" className="block py-2 text-gray-700 hover:text-yellow-600">Home</a>
              <a href="/about" className="block py-2 text-gray-700 hover:text-yellow-600">About</a>
              <a href="/results" className="block py-2 text-gray-700 hover:text-yellow-600">Results</a>
              <a href="/updates" className="block py-2 text-gray-700 hover:text-yellow-600">Updates</a>
              <a href="/contact" className="block py-2 text-yellow-600 font-medium">Contact</a>
              <a href="/terms" className="block py-2 text-gray-700 hover:text-yellow-600">Terms</a>
              <a href="/privacy" className="block py-2 text-gray-700 hover:text-yellow-600">Privacy</a>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-yellow-500 to-yellow-300 py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Contact Us
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            We're here to help! Get in touch with our friendly support team for any questions or assistance.
          </p>
        </div>
      </section>

      {/* Contact Information & Form Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Get in Touch</h2>
              
              <div className="space-y-6">
                <Card>
                  <CardContent className="p-6 flex items-start space-x-4">
                    <Mail className="w-6 h-6 text-yellow-600 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Email Support</h3>
                      <p className="text-gray-700 mb-2">For general inquiries and support</p>
                      <a href="mailto:support@goldenticketlucky.com" className="text-yellow-600 hover:text-yellow-700 underline">
                        support@goldenticketlucky.com
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6 flex items-start space-x-4">
                    <Phone className="w-6 h-6 text-yellow-600 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Phone Support</h3>
                      <p className="text-gray-700 mb-2">Available Monday to Friday, 9 AM - 6 PM AEST</p>
                      <a href="tel:1800123456" className="text-yellow-600 hover:text-yellow-700 underline">
                        1800 123 456
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6 flex items-start space-x-4">
                    <MapPin className="w-6 h-6 text-yellow-600 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Head Office</h3>
                      <p className="text-gray-700">
                        Level 10, 123 Collins Street<br />
                        Melbourne, VIC 3000<br />
                        Australia
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6 flex items-start space-x-4">
                    <Clock className="w-6 h-6 text-yellow-600 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">Business Hours</h3>
                      <div className="text-gray-700">
                        <p>Monday - Friday: 9:00 AM - 6:00 PM AEST</p>
                        <p>Saturday: 10:00 AM - 4:00 PM AEST</p>
                        <p>Sunday: Closed</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card>
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-gray-900 text-center mb-6">Send us a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                        Email Address *
                      </label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-gray-700 font-medium mb-2">
                        Subject *
                      </label>
                      <Input
                        id="subject"
                        type="text"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        className="w-full"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="w-full"
                        placeholder="Please describe your inquiry..."
                      />
                    </div>
                    
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="agreement"
                        checked={agreedToTerms}
                        onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                        className="mt-1"
                      />
                      <label htmlFor="agreement" className="text-sm text-gray-700">
                        I agree to the{' '}
                        <a href="/terms" className="underline text-yellow-600">Terms and Conditions</a>
                        {' '}and{' '}
                        <a href="/privacy" className="underline text-yellow-600">Privacy Policy</a>
                      </label>
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={!agreedToTerms}
                      className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 py-3"
                    >
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Gambling Help Section */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Need Gambling Support?</h2>
            <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
              If you're concerned about your gambling or need someone to talk to, help is available 24/7.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <Card>
                <CardContent className="p-8 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">GambleAware NSW</h3>
                  <p className="text-gray-700 mb-4">Free, confidential support and counselling services</p>
                  <a 
                    href="https://gambleaware.nsw.gov.au" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-yellow-600 underline hover:text-yellow-700"
                  >
                    Visit GambleAware NSW
                  </a>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-8 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">BetStop</h3>
                  <p className="text-gray-700 mb-4">National Self-Exclusion Register</p>
                  <a 
                    href="https://betstop.gov.au" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-yellow-600 underline hover:text-yellow-700"
                  >
                    Visit BetStop
                  </a>
                </CardContent>
              </Card>
            </div>
            <div className="mt-8">
              <p className="text-2xl font-bold text-gray-900">24/7 Gambling Help Line</p>
              <a href="tel:+61359294105" className="text-3xl font-bold text-yellow-600 hover:text-yellow-700">
                +61 35 9294 105
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold text-yellow-500 mb-4">GoldenTicketLucky</h3>
              <p className="text-gray-300 text-sm">
                Dream big, play responsibly. Your trusted Australian lottery experience.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <a href="/" className="block text-gray-300 hover:text-yellow-500">Home</a>
                <a href="/about" className="block text-gray-300 hover:text-yellow-500">About Us</a>
                <a href="/results" className="block text-gray-300 hover:text-yellow-500">Draw Results</a>
                <a href="/contact" className="block text-gray-300 hover:text-yellow-500">Contact</a>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Legal</h4>
              <div className="space-y-2 text-sm">
                <a href="/terms" className="block text-gray-300 hover:text-yellow-500">Terms & Conditions</a>
                <a href="/privacy" className="block text-gray-300 hover:text-yellow-500">Privacy Policy</a>
                <a href="/cookies" className="block text-gray-300 hover:text-yellow-500">Cookie Policy</a>
                <a href="/accessibility" className="block text-gray-300 hover:text-yellow-500">Accessibility</a>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Get Help</h4>
              <div className="space-y-2 text-sm">
                <a href="https://gambleaware.nsw.gov.au" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-yellow-500">GambleAware</a>
                <a href="https://betstop.gov.au" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-yellow-500">BetStop</a>
                <p className="text-gray-300">+61 35 9294 105</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-sm text-gray-300">
            <p>© 2025 GoldenTicketLucky. All rights reserved. | Licensed under NSW Lotteries Act | 18+ only</p>
            <p className="mt-2">
              BetStop — National Self-Exclusion Register. A free tool to opt out of online wagering. Visit{' '}
              <a href="https://betstop.gov.au" target="_blank" rel="noopener noreferrer" className="text-yellow-500 underline">
                betstop.gov.au
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
